import {
  setCertificateInstances,
  setIsCertificateInstancesRequested,
  setIsCertificatesRequested,
} from '../../../Store/DataSlice'
import './CertificateInstanceDetail.scss'
import { useEffect } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import { useDispatch, useSelector } from 'react-redux'
import { type RootState } from '../../../Store/Store'
import { CertificateList } from '../../Certificate/CertificateList'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { Button } from '../../../Components/Button/Button'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'

interface CertificateInstanceDetailProps {
  vatNumber: string
}
export function CertificateInstanceDetail({
  vatNumber,
}: CertificateInstanceDetailProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate-instance')
  const countryLocalization = useLocalization('country')

  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )
  const certificateInstance = allCertificateInstances.find(
    (c) => c.vatNumber === vatNumber
  )

  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  const filteredCertificates =
    certificateInstance?.vatNumber != null
      ? allCertificates.filter(
          (c) =>
            c.certificateInstanceVatNumber === certificateInstance.vatNumber
        )
      : []

  const user = useSelector((state: RootState) => state.authentication.user)

  const removeCertificateInstance = () => {
    if (
      certificateInstance == null ||
      allCertificates.length === 0 ||
      filteredCertificates.length > 0
    )
      return

    if (
      !confirm(
        localization.text(
          'confirm-certificate-instance-delete',
          'Bent u zeker dat u deze certificaat instantie wilt verwijderen?'
        )
      )
    )
      return

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-instance-delete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificateInstanceVatNumber: certificateInstance.vatNumber,
        }),
      }
    )
      .then(() => {
        dispatch(
          setCertificateInstances(
            allCertificateInstances.filter(
              (c) => c.vatNumber !== certificateInstance.vatNumber
            )
          )
        )
        setTimeout(() => {
          dispatch(
            setDestinationView({ destinationView: 'certificate-instance-list' })
          )
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-instance-delete failed: `, err)
      })
  }

  return (
    <div className="certificate-instance-detail">
      {!certificateInstance ? (
        <div className="loading">
          {localization.text(
            'certificate-instance-is-loading',
            'De certificaat instantie word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          {user?.isAdmin ?? false ? (
            <div className="edit-button-wrapper">
              <Button
                size="regular"
                importance="primary"
                onClick={() =>
                  dispatch(
                    setDestinationView({
                      destinationView: 'certificate-instance-edit',
                      detailKey: certificateInstance.vatNumber,
                    })
                  )
                }
                text={localization.text(
                  'edit',
                  'Editeer certificaat instantie'
                )}
              />
              <Button
                size="regular"
                importance="primary"
                onClick={removeCertificateInstance}
                disabled={
                  allCertificates.length === 0 ||
                  filteredCertificates.length > 0
                }
                text={localization.text('delete', 'certificaat instantie')}
              />
            </div>
          ) : null}
          <div className="display-field">
            <div className="display-label">
              {localization.text('name', 'Naam')}
            </div>
            <div className="display-value">{certificateInstance.name}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('email', 'Email')}
            </div>
            <div className="display-value">
              {certificateInstance.email ? (
                <a href={`mailto:${certificateInstance.email}`}>
                  {certificateInstance.email}
                </a>
              ) : null}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('website', 'Website')}
            </div>
            <div className="display-value">
              {certificateInstance.website ? (
                <a href={certificateInstance.website}>
                  {certificateInstance.website}
                </a>
              ) : null}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('telephone', 'Telefoon')}
            </div>
            <div className="display-value">
              {certificateInstance.telephone ? (
                <a href={`tel:${certificateInstance.telephone}`}>
                  {certificateInstance.telephone}
                </a>
              ) : null}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('addressLine1', 'Straat en huisnummer')}
            </div>
            <div className="display-value">
              {certificateInstance.addressLine1}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('postalCode', 'Postcode')}
            </div>
            <div className="display-value">
              {certificateInstance.postalCode}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('city', 'Gemeente')}
            </div>
            <div className="display-value">{certificateInstance.city}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('country', 'Land')}
            </div>
            <div className="display-value">
              {countryLocalization.text(
                certificateInstance.countryCode,
                certificateInstance.countryCode
              )}
            </div>
          </div>
          <h3>{localization.text('coordinators', 'VCA coordinators')}</h3>
          {certificateInstance.coordinators.map((coordinator) => (
            <div className="coordinator" key={coordinator.uuid}>
              <div className="display-field">
                <div className="display-label">
                  {localization.text('coordinator-name', 'Naam')}
                </div>
                <div className="display-value">{coordinator.name}</div>
              </div>
              <div className="display-field">
                <div className="display-label">
                  {localization.text('coordinator-email', 'Email')}
                </div>
                <div className="display-value">{coordinator.email}</div>
              </div>
              <div className="display-field">
                <div className="display-label">
                  {localization.text('coordinator-telephone', 'Telefoon')}
                </div>
                <div className="display-value">{coordinator.telephone}</div>
              </div>
            </div>
          ))}

          <div className="display-field">
            <div className="display-label">
              {localization.text('certificates', 'Certificaten')}
            </div>
            <CertificateList
              certificates={filteredCertificates}
              isLoading={allCertificates.length === 0}
            />
          </div>
        </>
      )}
    </div>
  )
}
